import store from '../store'
import { getCurrencyCode, centsToAmount, orderLineItems } from '@/utils/orderPricing'

// Meta (Facebook) Pixel e-commerce tracking. The pixel snippet itself is injected in App.vue only
// when branding.marketing_ids.meta_pixel_identifier is set, so every helper here no-ops on chains
// without a pixel configured.
//
// Meta only treats an event as an e-commerce event when it carries value + currency + content data,
// otherwise Events Manager falls back to showing automatically detected button clicks. Keep the
// param shape below aligned with https://developers.facebook.com/docs/meta-pixel/reference.

export default {
  methods: {
    // `fbq` alone isn't enough: a client's own GTM Meta tag also defines it. The app sends its Meta
    // events only when the chain's branding configures a pixel, so clearing meta_pixel_identifier
    // really hands Meta over to GTM instead of double-firing through GTM's pixel.
    metaPixelReady() {
      return typeof fbq === 'function' && Boolean(store.state.branding?.marketing_ids?.meta_pixel_identifier)
    },

    // Store/location context. Not a Meta standard param, but it lets the client segment by location.
    metaStoreParams() {
      const selectedStore = store.state.selectedStore
      const restaurant = store.state.orderData?.restaurant
      const storeId = selectedStore?.store_id ?? restaurant?.store_id
      const storeName = selectedStore?.name ?? restaurant?.name
      let params = {}
      if (storeId != null) params.store_id = String(storeId)
      if (storeName) params.store_name = storeName
      return params
    },

    metaContentsFromOrderItems(items) {
      const lineItems = orderLineItems(items)
      const contents = lineItems.map((line) => ({
        id: line.id,
        quantity: line.quantity,
        item_price: line.unitPrice,
        title: line.name,
      }))
      return {
        contents: contents,
        content_ids: contents.map((content) => content.id),
        content_type: 'product',
        num_items: lineItems.reduce((sum, line) => sum + line.quantity, 0),
      }
    },

    // eventId lets Meta dedupe when the same order is reported twice (e.g. the direct place-order
    // response and the payment poller both resolving, or a Conversions API event for the same order).
    trackMetaEvent(eventName, params, eventId) {
      if (!this.metaPixelReady()) return
      try {
        if (eventId) {
          fbq('track', eventName, params, { eventID: String(eventId) })
        } else {
          fbq('track', eventName, params)
        }
      } catch (error) {
        console.log('Meta Pixel tracking error:', error)
      }
    },

    trackMetaViewContent(content) {
      if (!this.metaPixelReady()) return
      // Store params come first so callers can override them — the guest is still picking a store
      // when this fires, so vuex may still hold the previously selected one.
      this.trackMetaEvent('ViewContent', {
        currency: getCurrencyCode(),
        ...this.metaStoreParams(),
        ...(content || {}),
      })
    },

    // itemData is a menu item detail response; totalCents is the configured price (modifiers and
    // quantity included) so the value matches what the guest actually added.
    trackMetaAddToCart(itemData, totalCents, quantity) {
      if (!this.metaPixelReady()) return
      const qty = Number(quantity) || 1
      const value = centsToAmount(totalCents)
      this.trackMetaEvent('AddToCart', {
        content_type: 'product',
        content_ids: [String(itemData?.item_id ?? '')],
        content_name: itemData?.item_name ?? '',
        contents: [
          {
            id: String(itemData?.item_id ?? ''),
            quantity: qty,
            item_price: centsToAmount(totalCents / qty),
            title: itemData?.item_name ?? '',
          },
        ],
        num_items: qty,
        currency: getCurrencyCode(),
        value: value,
        ...this.metaStoreParams(),
      })
    },

    trackMetaInitiateCheckout(order, serviceName) {
      if (!this.metaPixelReady()) return
      const orderData = order || store.state.orderData?.order
      if (!orderData) return
      const charges = orderData.charges || {}
      const valueCents = charges.total_cents ?? charges.subtotal_cents
      this.trackMetaEvent('InitiateCheckout', {
        ...this.metaContentsFromOrderItems(orderData.items),
        ...(serviceName ? { delivery_category: serviceName } : {}),
        currency: getCurrencyCode(),
        value: centsToAmount(valueCents),
        ...this.metaStoreParams(),
      })
    },

    trackMetaCompleteRegistration() {
      this.trackMetaEvent('CompleteRegistration', {})
    },

    trackMetaPurchase(order, serviceName) {
      if (!this.metaPixelReady()) return
      const orderData = order || store.state.orderData?.order
      if (!orderData) return
      const charges = orderData.charges || {}
      this.trackMetaEvent(
        'Purchase',
        {
          ...this.metaContentsFromOrderItems(orderData.items),
          ...(serviceName ? { delivery_category: serviceName } : {}),
          order_id: String(orderData.order_id ?? ''),
          currency: getCurrencyCode(),
          value: centsToAmount(charges.total_cents),
          ...this.metaStoreParams(),
        },
        orderData.order_id
      )
    },
  },
}
